'use strict'

export let english={
  definition:"definition",
  aspects: {
    time: "Time",
    scope: "Scope",
    cost: "Cost",
    quality: "Quality",
    risk: "Risk",
    scopeDescription: "Scope described in separate section."
  },
  background: {
    title:"Background",
    fields: {
      background: {
        title: "Why is this project initiated?",
        description:"Justification for the organizational activity (project)",
        help:`What is the business need this project is intended to address?
        What is the problem we are trying to solve?
        How do we know it is a problem?
        Who are we solving the problem for, and how do we know they want this unmet need to be solved?`
      },
      timing: {
        title:"Why now?",
        description:"Justification for the timing",
        help:`What is the trigger that makes us want to do this right now?
        Any external circumstances or internal factors? Any new insight, new technology or new strategy triggering the timing of this opportunity?`
      },
      bigPicture: {
        title: "The bigger picture",
        description:"Describe how the project is linked to higher level strategies or initiatives",
        help:`Is this project part of a larger strategic initiative?
        How is it linked to the business strategy?`
      }
    }
  },
  objectives: {
    title: "Objectives",
    fields: {
      benefitsDescription: {
        title: "Key Benefits to be Delivered",
        description:"The planned measurable improvements from this project perceived as an advantage by one or more stakeholders",
        help:`What are the measurable improvements this project will deliver?
        Which benefits are more important and which are less important?`
      },
      objectivesSummary: {
        title: "Project objectives",
        description: `Drag and drop the performance aspects to place them in the correct prioritized order.
          If there is no specific objective on an aspect, leave it open. Aspects without objective description will not appear in Brief/PID.`,
        help:`What are the performance goals for this project?
Any ambitions or boundaries the project should use as input to planning?
What are the project’s Key Performance Indicators (KPIs)?

Time:
-	Is there a time frame or deadline we need to meet?
-	Is there a business reason for this deadline?
Time Tolerance: How much can the project forecast exceed the time objective before a notification is escalated beyond the Project Board?

Scope: (Do not start listing scope details or product features here)
-	What is it we are aiming to achieve by defining the scope like we are?
-	How far does this project go? In short, what will the project take responsibility for?
-	Is the scope wide or narrow? If the scope is wide, why is it wide?
-	If the scope is narrow, why is it narrow?
Scope Tolerance: How much can the scope change before the Project Board notifies the next level (or customer)?

Cost:
- What do we expect or tolerate to spend on this project?
– Why do we have this cost objective? What are the reasons behind it?
Cost Tolerance: When will awareness (or approval) of a forecasted change in cost have to be escalated beyond the Project Board?

Quality: (Do not start listing detailed requirements or product features here)
-	What is the guiding star when defining the key characteristics of the main deliverables in this project?
-	What is the summarized objective of the key requirements, standards and regulations for the project's output?
Quality Tolerance: How different can the main deliverables be before the Project Board should involve the next governance level (or the customer)?

Risk: (Do not list specific project risks here)
-	What is an acceptable risk level for this project?
-	How is the stakeholders’ risk appetite?
-	Any specific risk objectives related to regulations, classified information or data privacy?
Risk Tolerance: What is an acceptable risk level to be dealt with at the Project Board level?
When is the accumulated risk exposure triggering a formal involvement beyond the Project Board?


Priorities:
When trade-offs have to be made, which one of the project objectives cannot be sacrificed?
Explain the consequence of missing each of the priorities. Then, having considered that, which one is the most important driver.
 `,
        columns: {
          pri: { title: "Pri" },
          name: { title: "Performance aspect" },
          description: {
            title: "Objective description"
/*
description: {
time: "Describe when the project should be completed and/or the most important milestones",
scope: "Describe the objective of scoping the project a certain way. How wide or how narrow will the scope have to be to succeed? Don’t list the scope elements",
cost: "Define the cost target for this project. If relevant, split into different types of cost",
quality: "Describe to what extent the project shall meet all (possible) requirements, standards and regulations. Do not list all requirements, but if important above all the rest, one or a few key requirements can be described.",
risk: "Define if the risk appetite is high or low. Quantify if possible. Don’t list any risks."
}
*/
          },
          tolerance: { title: "Tolerance" }
        }
      },
      objectives: {
        title: "Comments to objectives",
        description:"Other relevant information related to performance goals for this project"
      }
    }
  },
  scope: {
    title: "Scope",
    fields: {
      projectScope: {
        title: "Scope",
        description: "Drag and drop the scope items in the correct prioritized order",
        help:`What are we designing/making? 
              Is the project also taking care of:
              1. Preparing launch?
              2. Training of users, customers or internal functions?
              3. Preparing any services or service functions?
              4. Phasing out any old solutions?
              5. Migration of data or equipment from old solution?
              6. Integration with other solutions?
              7. Preparing efficient production, operation and maintenance?
              8. Targeting all users, geographies/languages, functions, segments?
              9. Any follow-up responsibilities after roll-out?`,
        samples: [
          { text: "Something to build or design? For a specific department, function , geography or use?" },
          { text: "Prepare any production/operation environment? Where?" },
          { text: "Prepare any service or support functions? Which ones? How?" },
          { text: "Roll-out/launch, prepare or train any users, sales organizations, customers? Where?" }
        ],
        columns: {
          text: { title: "Break scope into separate short statements. One per line below." }
        }
      },
      scopeExclusions: {
        title: "Exclusions to scope",
        description:"Describe what the project will not deliver.",
        help:"Are there specific things the project will not deliver? Are there any functions, users, geographies or departments not included?"
      },
      outcomes: {
        title: "Expected Outcomes",
        description:"The result of change, normally affecting real-world behavior and/ or circumstances.",
        help:"How will the project enable your organization to serve its customers or users better? What new things (ability) can our customers (internal and external) do when the project is completed?"
      }
    }
  },
  constraints: {
    title: "Constraints",
    fields: {
      constraints: {
        title: "Constraints",
        description:"Restrictions or limitations the project is bound by",
        help:`Are there things other projects need to deliver into this project?
          Are there any pre-standing commitments or expectations to other organizations or partners that need to be met? Timing commitments? 
          Are there any known practical limitations to execute this project?
          1. The customer is expecting a certain type of solution
          2. Product price constraint
          3. Timing constraint – this opportunity is temporary, as other competitors may move into the market.
          Will any special information security constraints apply to the project? Will the project deal with classified information?`
      },
      assumptions: {
        title: "Assumptions",
        description:"What is taken as being true for the purpose of planning, but which could change later. An assumption is made where some facts are not yet known or decided.",
        help:`Are there any assumptions about access to resources, tools, technology or competencies?
              Any assumptions about the quality of existing data or equipment?
              Any assumptions made about what other solutions will cover?`
      }
    }
  },
  stakeholders: {
    title:"Stakeholders & interfaces",
    fields: {
      stakeholderDescription: {
        title: "Stakeholders",
        description:"The users and any other knows interested parties",
        help:`For whose benefit is the project being undertaken?
          Who is going to use the project’s products? (Sales, Sourcing, etc.)
          What customer group or department is the project intended to affect?
          Who is likely to feel threatened by this project or have strong opinions?
          Who needs to be happy with the result?
          Who are the strong supporters of this project?
          What partners are involved? 
          What vendors could be involved? `
      },
      interfaces: {
        title: "Interfaces",
        description:"Describe the project’s interfaces",
        help:`Is this project depending on other projects or activities?
          Is the system or product connected in any way or working with other systems or products? 
          Are there any non-technical systems the project’s products will need to interface with during their lifecycle or use?`
      }
    }
  },
  messages: {
    updateScope: "Please update scope before you use Project Product Description in 'Quality'.",
    deletedScope: "(deleted: please remove outputs)"
  }
}



// WEBPACK FOOTER //
// ./views/definition/labels/english.js